import React, { useState } from 'react';
import { Badge, Button, Input } from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { faint, STATUS, TYPE_LABELS } from './format';

/** Shared bits for the Backlinks tabs and their side panels, so the four views read as one screen. */

/** Domain Rating as a mono figure, toned by tier: 70+ strong, 50+ good, below that plain. */
export function DRBadge({ dr }: { dr: number | null }) {
  if (dr == null) return <span style={faint}>—</span>;
  const tone: BadgeTone = dr >= 70 ? 'up' : dr >= 50 ? 'info' : 'neutral';
  return (
    <Badge tone={tone} style={{ minWidth: '34px', justifyContent: 'center' }} title={`Domain Rating ${Math.round(dr)}`}>
      {Math.round(dr)}
    </Badge>
  );
}

export function TypeLabel({ type }: { type: string }) {
  return <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{TYPE_LABELS[type] || type}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS[status];
  return <Badge tone={s?.tone ?? 'neutral'}>{s?.label ?? status}</Badge>;
}

/** Relevance 0-1 as a five-segment meter. */
export function RelevanceMeter({ score }: { score: number | null }) {
  if (score == null) return <span style={faint}>—</span>;
  const filled = Math.round(score * 5);
  return (
    <span title={`Relevance ${score.toFixed(2)}`} style={{ display: 'inline-flex', gap: '3px', verticalAlign: 'middle' }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: '10px',
            height: '6px',
            borderRadius: '2px',
            background: i < filled ? 'var(--accent)' : 'var(--surface-raised)',
            border: i < filled ? 'none' : '1px solid var(--border-hairline)',
          }}
        />
      ))}
    </span>
  );
}

/** A count-labelled option for SegmentedControl: "Dofollow 12", the count quiet. */
export function CountLabel({ label, n }: { label: string; n: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
      {label}
      <span className="t-num" style={{ color: 'var(--text-faint)', fontSize: 'var(--text-xs)' }}>
        {n}
      </span>
    </span>
  );
}

/** Panel section: mono eyebrow over a stack of fields. */
export function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
      <span className="t-eyebrow">{title}</span>
      {children}
    </section>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 'var(--space-3)', alignItems: 'baseline' }}>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

/** Three metrics side by side at the top of a panel. */
export function MetricRow({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-6)',
        overflow: 'hidden',
      }}
    >
      {items.map((it, i) => (
        <div
          key={it.label}
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderLeft: i ? '1px solid var(--border-hairline)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <span className="t-eyebrow">{it.label}</span>
          <span className="t-num" style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>
            {it.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline "why?" field that replaces the native prompt(). Shows a text input with confirm/cancel;
 * confirm stays disabled until there is a reason.
 */
export function ReasonField({
  placeholder,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  placeholder: string;
  confirmLabel: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const submit = () => reason.trim() && onConfirm(reason.trim());
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
      <Input
        autoFocus
        fieldSize="sm"
        placeholder={placeholder}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onCancel();
        }}
        style={{ flex: 1, minWidth: 0 }}
      />
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="sm" variant="primary" onClick={submit} disabled={!reason.trim()}>
        {confirmLabel}
      </Button>
    </div>
  );
}

